import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {withRouter, RouteComponentProps, useHistory, useLocation} from "react-router";
import { Container, Box } from "@material-ui/core";
import { IdentityApplicationEnvironmentType } from "libs/resources/identity/applications/PublisherApplicationService";
import { useIdentityApplicationInfo } from "libs/resources/identity/applications/application-views/IdentityApplicationInfo";
import { Alert } from "@material-ui/lab";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useSelector } from "react-redux";
import { AsyncLoader } from "legacy-components/form/action/AsyncAction";
import ApplicationSummaryDetailView from "./ApplicationSummaryDetailView";
import GenericPageTitle from "modus/components/generic-page-title/GenericPageTitle";
import { resolveAPIErrorWithCode } from "modus/components/error-message/ErrorMessage";
import { ErrorMessages } from "pages/home/subscriptions/components/unsubscribed-application-list/ErroCodeMapping";
import { Breadcrumbs } from "modus/components/generic-page-title/Breadcrumbs";
import {AuthorizationProvider} from "../../../authorizer/components/AuthorizationProvider";
import { ResourceAuthorizer } from "pages/home/authorizer/components/Authorizer";
import {
  ApplicationSummaryPageContext,
  DeploymentPageAlertType,
} from "./ApplicationSummaryPageContext";

export type { DeploymentPageAlertType };
export type { IApplicationSummaryPageContext } from "./ApplicationSummaryPageContext";
export { ApplicationSummaryPageContext };

interface RouterPathProps {
  id?: string;
  environmentName?: IdentityApplicationEnvironmentType;
}

interface ApplicationSummaryPageProps {}

const supportRegionalDeployments =
  process.env.REACT_APP_SUPPORT_REGIONAL_DEPLOYMENTS === "true";

const DEPLOYMENT_PAGE_ALERT_AUTO_DISMISS_MS = 10_000;

function ApplicationSummaryPageContent({
  match,
}: RouteComponentProps<RouterPathProps> & ApplicationSummaryPageProps) {
  const history = useHistory();
  const identityEnvironment = useSelector(
    (state: any) => state?.common?.identityEnvironment
  );

  const apiName = new URLSearchParams(window.location.search).get('apiName');
  
  const { state, dispatch, response, subscribe: reloadApplication } = useIdentityApplicationInfo({
    applicationName: apiName || match.params.id,
    environmentName: match.params.environmentName,
  }
  );



  // Add state for API CONFIGURATION sub-tabs
  const [apiConfigTabValue, setApiConfigTabValue] = useState(0);

  const [deploymentPageAlert, setDeploymentPageAlert] =
    useState<DeploymentPageAlertType>(null);

  const notifyDeploymentAddedOnPage = useCallback(
    () => setDeploymentPageAlert("added"),
    []
  );
  const notifyDeploymentUpdatedOnPage = useCallback(
    () => setDeploymentPageAlert("updated"),
    []
  );
  const dismissDeploymentPageAlert = useCallback(
    () => setDeploymentPageAlert(null),
    []
  );

  useEffect(() => {
    if (!supportRegionalDeployments || deploymentPageAlert == null) {
      return;
    }
    const timer = window.setTimeout(
      dismissDeploymentPageAlert,
      DEPLOYMENT_PAGE_ALERT_AUTO_DISMISS_MS
    );
    return () => window.clearTimeout(timer);
  }, [deploymentPageAlert, dismissDeploymentPageAlert]);

  const [viewContext, setViewContext] = useState({
    state,
    dispatch,
    apiConfigTabValue,
    setApiConfigTabValue,
    deploymentPageAlert,
    notifyDeploymentAddedOnPage,
    notifyDeploymentUpdatedOnPage,
    dismissDeploymentPageAlert,
  });

  useEffect(() => {
    if (!match.params.environmentName || !identityEnvironment || !history) {
      return;
    }
    if (match.params.environmentName !== identityEnvironment) {
      history.push(`/home/apis`);
    }
  }, [match.params.environmentName, identityEnvironment, history]);

  useEffect(() => {
    setViewContext((prev) => ({
      ...prev,
      state,
      dispatch,
      apiConfigTabValue,
      setApiConfigTabValue,
      deploymentPageAlert,
      notifyDeploymentAddedOnPage,
      notifyDeploymentUpdatedOnPage,
      dismissDeploymentPageAlert,
    }));
  }, [
    state,
    dispatch,
    apiConfigTabValue,
    deploymentPageAlert,
    notifyDeploymentAddedOnPage,
    notifyDeploymentUpdatedOnPage,
    dismissDeploymentPageAlert,
  ]);

  const location = useLocation();
  const apiId = location.state as string | null;

  return (
      <ResourceAuthorizer resourceType="APPLICATION" resourceId={apiId || ""} action="read">
      {response?.error && (
        <Alert severity="error">
          {resolveAPIErrorWithCode(response.error, ErrorMessages)}
        </Alert>
      )}
      <ApplicationSummaryPageContext.Provider value={viewContext}>
        <Box padding={2} className="app-discovery-page">
          {!response?.error && (
            <>
              {supportRegionalDeployments &&
                deploymentPageAlert === "updated" && (
                  <Alert
                    severity="success"
                    onClose={dismissDeploymentPageAlert}
                    icon={<InfoOutlinedIcon />}
                    style={{
                      marginBottom: 16,
                      backgroundColor: "#DFE9D6",
                      color: "#4A821F",
                    }}
                  >
                    Deployment updates have been saved.
                  </Alert>
                )}
              {supportRegionalDeployments &&
                deploymentPageAlert === "added" && (
                  <Alert
                    severity="success"
                    onClose={dismissDeploymentPageAlert}
                    icon={<InfoOutlinedIcon />}
                    style={{
                      marginBottom: 16,
                      backgroundColor: "#DFE9D6",
                      color: "#4A821F",
                    }}
                  >
                    Deployment added successfully.
                  </Alert>
                )}
              <Breadcrumbs
                links={[{ text: "Apis", href: "/home/apis" }]}
                pageTitle={state?.application?.displayName}
              />
              <GenericPageTitle
                title={state?.application?.displayName}
                description={
                  state?.application?.description === null ||
                  state?.application?.description === "" ||
                  state?.application?.description === undefined
                    ? state?.application?.displayName
                    : state?.application?.description
                }
                noControls
                noSubTitle
                // To be enabled once the feature is available
                // badge={
                //     <Box style={{ marginLeft: "15px", paddingLeft: "15px" }}>
                //       <Badge color="primary" badgeContent={"Approved"} />
                //     </Box>}
              />
            </>
          )}
        </Box>
        <Container maxWidth={false} style={{ margin: "0px", width: "100%" }}>
          <AsyncLoader
            trigger={!!state.application}
            state={response}
            loader={<></>}
          >
            {state.application && <ApplicationSummaryDetailView  reloadApplication={reloadApplication as any}/>}
          </AsyncLoader>
        </Container>
      </ApplicationSummaryPageContext.Provider>
    </ResourceAuthorizer>
  );
}

ApplicationSummaryPageContent.propTypes = {
  match: PropTypes.any.isRequired,
};

const ApplicationSummaryPage: React.FC<RouteComponentProps<RouterPathProps>> = (props) => {
  // Use id from path params for authorization
  const location = useLocation();
  const apiId = location.state as string | null;

  return (
      <AuthorizationProvider
          resources={[
            {
              resourceType: "APPLICATION",
              resourceId: apiId || ""
            },
          ]}
          resourceType="APPLICATION"
      >
        <ApplicationSummaryPageContent {...props} />
      </AuthorizationProvider>
  );
};

export default withRouter(ApplicationSummaryPage);
